import { Alert, Anchor, Card, Grid, SegmentedControl, Switch, Text, TextInput } from '@mantine/core';
import { IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import LabeledSlider from '../../LabeledSlider';
import LazyLoaderSpinner from '../../LazyLoaderSpinner';
import DomainEmailVerificationModal from '../../Modals/DomainEmailVerificationModal';
import { SenderDomainMailAlert } from '../../SenderDomainMailHealth';
import Spinner from '../../Spinner';
import SettingsTabKlaviyo from './SettingsTabKlaviyo';
import SuperAdminArea from '../../SuperAdmin/SuperAdminArea';
import useIsSuperAdmin from '../../../helpers/useIsSuperAdmin';
import EmailHoldsAdminPanel from '../EmailHoldsAdminPanel';
import EmailSendingHoldAlert from '../../EmailSendingHoldAlert';

const SettingsTabEmails = ({ domain, currentDomain, setDomain, currentAccount, handleChange }) => {
  const fetch = useFetch();
  const location = useLocation();
  const [verificationDetailsOpen, setVerificationDetailsOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState();
  const [error, setError] = useState(false);
  const [searchParams] = useSearchParams();
  const emailDnsVerificationOpen = searchParams.get('emailDnsVerificationOpen');
  // Bumped when a super admin releases a hold, so the customer-facing banner re-reads
  // the enforced-holds route instead of leaving a released pause on screen.
  const [holdsReloadToken, setHoldsReloadToken] = useState(0);
  // A domain connected to Klaviyo does not send email through Releva at all, so the
  // Releva sending settings below are hidden and replaced by an info alert. Managing
  // the connection itself is super-admin only for now.
  const klaviyoAccount = useSelector((state) => state.klaviyo.account);
  const klaviyoLoading = useSelector((state) => state.klaviyo.loading);
  const klaviyoConnected = Boolean(klaviyoAccount && !klaviyoAccount.credentialsInvalid);

  const isSuperAdmin = useIsSuperAdmin();

  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
  }, [location]);

  // Sender verification is a single boolean now that both required DNS records are
  // checked by Releva itself - there is no pending state to report.
  const getVerificationStatusHuman = (verified) => {
    return verified ? (
      <>
        <IconCheck size={14} color="var(--mantine-color-green-6)" /> successful
      </>
    ) : (
      <>
        <IconX size={14} color="var(--mantine-color-red-6)" /> failed
      </>
    );
  };

  const refreshVerificationStatus = useCallback(
    (cb) => {
      fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/verificationStatus`)
        .then((verificationStatus) => {
          setVerificationStatus(verificationStatus);
          if (typeof cb === 'function') {
            cb();
          }
        })
        .catch(setError);
    },
    [currentDomain.accountId, currentDomain.id, fetch]
  );

  useEffect(() => {
    // Verification status only matters for Releva-sent email - a Klaviyo-connected domain
    // does not send through Releva, so skip the DNS-status request entirely for it. Also
    // wait until the klaviyo account fetch has resolved (loading === false), otherwise the
    // first mount fires one wasted DNS request before the connection state is known.
    if (klaviyoConnected || klaviyoLoading !== false) {
      return;
    }
    refreshVerificationStatus();
  }, [currentDomain?.senderEmail, refreshVerificationStatus, klaviyoConnected, klaviyoLoading]);

  useEffect(() => {
    setVerificationDetailsOpen(Boolean(emailDnsVerificationOpen));
  }, [emailDnsVerificationOpen]);

  // Verification status only matters for Releva-sent email - don't let a DNS fetch
  // error or spinner block the tab for Klaviyo-connected domains.
  if (!klaviyoConnected && error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!klaviyoConnected && verificationStatus === undefined) {
    return <Spinner />;
  }

  if (klaviyoConnected) {
    return (
      <>
        <Alert
          color="blue"
          icon={<IconInfoCircle />}
          mb="md"
          title="Email delivery for this domain is handled by Klaviyo"
        >
          <Text size="sm">
            Releva's own email sending is turned off for this domain, and email engagement plus subscribe/unsubscribe
            data syncs in from Klaviyo automatically.
          </Text>
          <Text size="sm" mt="xs">
            You can keep building campaigns in Releva - email steps are delivered through Klaviyo: each send fires a
            "Releva Send Email" event and a Live Klaviyo flow triggered on that metric delivers the email. All other
            campaign channels (SMS, Viber, Whatsapp, push, webhooks) work as usual.
          </Text>
        </Alert>
        <SuperAdminArea show={isSuperAdmin}>
          <SettingsTabKlaviyo klaviyoAccount={klaviyoAccount} />
        </SuperAdminArea>
      </>
    );
  }

  return (
    <Card withBorder>
      <Card.Section p="md">
        <EmailSendingHoldAlert
          accountId={currentDomain.accountId}
          domainId={currentDomain.id}
          reloadToken={holdsReloadToken}
        />
        <DomainEmailVerificationModal
          data-tour="emailDnsVerificationModal"
          show={verificationDetailsOpen}
          close={() => setVerificationDetailsOpen(false)}
          verificationStatus={verificationStatus}
          getVerificationStatusHuman={getVerificationStatusHuman}
          refreshVerificationStatus={(cb) => refreshVerificationStatus(cb)}
          currentAccount={currentAccount}
          currentDomain={currentDomain}
        />
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Email Sender Name</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="senderName"
              placeholder="My Awesome Shop Team"
              value={domain.senderName}
              error={domain.senderName !== undefined && domain.senderName === '' ? 'sender name is required' : null}
              onChange={handleChange}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Email Sender Address</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="senderEmail"
              placeholder="info@shop.com"
              value={domain.senderEmail || ''}
              error={domain.senderEmail !== undefined && domain.senderEmail === '' ? 'sender email is required' : null}
              onChange={handleChange}
            />
            <Text size="xs" data-tour="emailDnsVerificationLink" mt="xs">
              {verificationStatus === undefined ? (
                <LazyLoaderSpinner />
              ) : (
                <>
                  Sender verification: {getVerificationStatusHuman(verificationStatus.isEmailSendingVerified)}
                  <Anchor
                    size="sm"
                    component="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setVerificationDetailsOpen(true);
                    }}
                    ml="xs"
                  >
                    details...
                  </Anchor>
                </>
              )}
            </Text>
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Email Reply-to Address</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="replyToEmail"
              placeholder="hello@shop.com"
              value={domain.replyToEmail}
              error={
                domain.replyToEmail !== undefined && domain.replyToEmail === '' ? 'reply to email is required' : null
              }
              onChange={handleChange}
            />
            {/* The failure this warns about is about where replies land, so it sits with
                the Reply-to address rather than with the sender verification line above -
                the detail is one click away in the modal. */}
            <SenderDomainMailAlert
              check={verificationStatus.releva?.senderDomainMail}
              onShowDetails={() => setVerificationDetailsOpen(true)}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Max Emails Per Day</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <LabeledSlider
              min={1}
              max={10}
              value={domain.maxEmailsPerDay}
              onChange={(maxEmailsPerDay) => setDomain({ ...domain, maxEmailsPerDay })}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Optimize for</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={String(domain.sendEmailsWithDelay)}
              onChange={(value) => setDomain({ ...domain, sendEmailsWithDelay: value === 'true' })}
              data={[
                { label: 'Sending Speed', value: 'false' },
                { label: 'Delivery Rate', value: 'true' },
              ]}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Sending speed optimization sends the emails faster which can make your emails go into spam folders.
              <br />
              Delivery rate optimization sends the emails slower and that decreases the chance that your emails will end
              up in the spam folder.
            </Text>
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Bypass Plus Addressing</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Switch
              checked={domain.bypassEmailPlusAddressing}
              onChange={(e) => setDomain({ ...domain, bypassEmailPlusAddressing: e.currentTarget.checked })}
            />

            <Text size="xs" c="dimmed" mt="xs">
              This setting allows you to bypass the +suffix of email addresses in your mailing lists.
              <br />
              This is helpful, if, for examples, users have signed up using for ex.{' '}
              <strong>john+folder1@example.com</strong> and have a rule to deliver your emails to folder1, but you wish
              to deliver email to their main mailbox instead.
            </Text>
          </Grid.Col>
        </Grid>
      </Card.Section>
      {isSuperAdmin && (
        <Card.Section p="md" pt={0}>
          <SuperAdminArea show>
            <Text size="sm" fw={500} mb="xs">
              Email sending holds
            </Text>
            <EmailHoldsAdminPanel
              domainId={currentDomain.id}
              onReleased={() => setHoldsReloadToken((token) => token + 1)}
            />
            <Text size="sm" fw={500} mb="xs" mt="md">
              Klaviyo
            </Text>
            <SettingsTabKlaviyo klaviyoAccount={klaviyoAccount} />
          </SuperAdminArea>
        </Card.Section>
      )}
    </Card>
  );
};

export default SettingsTabEmails;
